﻿import isNullOrEmpty from "../helpers/isNullOrEmpty";
import readNestedProperty from "../helpers/readNestedProperty";
import Locale, {DateFormat, DatetimeFormat, DurationFormat, NumberFormat, TimeFormat} from "../i18n/Locale";
import {ParserArgument} from "./ParserArgument";

export type ParserDelegate = (obj: Record<string, any>) => string;

function interpretElement(culture: Locale, element: ParserArgument, parent: ParserArgument | null): ParserDelegate {
	if (element.type === "text") {
		return () => element.id;
	}

	let id = element.id;
	let type = element.type;
	let offset = 0;
	if (id === "#") {
		if (parent == null) {
			throw new Error("The message requires the argument '#', but there is no parent argument.");
		}

		if (parent.type !== "plural" && parent.type !== "selectordinal") {
			throw new Error("The message requires the argument '#', but the parent argument is not a plural or selectordinal.");
		}

		// type stays "number" (from the parsed element) so the switch falls into number formatting
		id = parent.id;
		offset = parent.offset;
	}

	switch (type) {
		case "number":
		case "ordinal": //TODO: rbnf
		case "spellout": {
			//{completion, number, percent}
			//decimal | integer | percent | currency
			//t("completion", { completion: 0.85 });
			return (obj) => culture.formatNumber((+getArgumentValue(obj, id)) - offset, element.styleFormat as NumberFormat);
		}

		case "duration": {
			//{uni_time, duration, long}
			//full | long | medium | short
			return (obj) => culture.formatDuration((getArgumentValue(obj, id)), element.styleFormat as DurationFormat);
		}

		case "date": {
			//{uni_date, date, short}
			//full | long | medium | short | day_month | month_year | smonth_year
			return (obj) => culture.formatDate(getArgumentValue(obj, id), element.styleFormat as DateFormat);
		}

		case "time": {
			//{uni_time, time, long}
			//full | long | medium | short
			return (obj) => culture.formatTime(getArgumentValue(obj, id), element.styleFormat as TimeFormat);
		}

		case "datetime": {
			//{uni_datetime, datetime, full}
			//full | long | medium | short
			return (obj) => culture.formatDateTime(getArgumentValue(obj, id), element.styleFormat as DatetimeFormat);
		}

		case "plural":
		case "selectordinal": {
			// {count, plural,
			// 	one {You have # message.}
			// 	other {You have # messages.}
			// }
			// t("user_messages", { count: 9 });

			// {count, selectordinal,
			//     one {We planted our #st tree.}
			//     two {We planted our #nd tree.}
			//     few {We planted our #rd tree.}
			//     other {We planted our #th tree.}
			// }
			// t("trees_planted", { count: 3 });

			if (element.subMsgFormat == null) {
				throw new Error(`The message requires the plural or selectordinal argument '${id}', but there is no submessage format.`);
			}

			const plurals = makeSubFormats(culture, element.subMsgFormat, {
				id: id,
				offset: element.offset,
				styleFormat: null,
				subMsgFormat: null,
				type: type
			});

			return (obj) => {
				const value = getArgumentValue(obj, id);
				const interpreter = plurals[`=${value}`];
				if (interpreter != null) {
					return interpreter(obj);
				}
				const key = culture.selectPlural((+value) - element.offset, type === "selectordinal");
				return (plurals[key] || plurals["other"])(obj);
			};
		}

		case "select": {
			// {fruit, select,
			// 	apple {Let's pick apples!}
			// 	cherry {Let's pick cherries!}
			// 	other {Let's pick fruits!}
			// }
			// t("fruit_picking", { fruit: "cherry" });
			if (element.subMsgFormat == null) {
				throw new Error(`The message requires the select argument '${id}', but there is no submessage format.`);
			}

			let options = makeSubFormats(culture, element.subMsgFormat, null);
			return (obj) => (options[`${getArgumentValue(obj, id)}`] || options["other"])(obj);
		}

		case "list": {
			// {elements, list, [type]-[style]}
			// type: "conjunction" | "disjunction" | "unit"
			// style: "long" | "short" | "narrow"
			// t("fruit_picking", { fruit: "cherry" });

			if (element.styleFormat == null) {
				throw new Error(`The message requires the list argument '${id}', but there is no style format.`);
			}

			// styleFormat is fixed per message, so split it once at build time, not on every render.
			const [type, style] = element.styleFormat.split("-") as [Intl.ListFormatType, Intl.ListFormatStyle];
			return (obj) => {
				const values: any[] = getArgumentValue(obj, id) ?? [];
				//mapeo los valores a strings ya que si no lo son, tira error I18N
				return culture.formatCollection(values.map((x) => x.toString()), type, style);
			};
		}

		default: {
			//"Hello, {name}!"
			return (obj) => `${getArgumentValue(obj, id)}`;
		}
	}
}

function getArgumentValue(source: Record<string, any>, path: string): any {
	if (source != null) {
		return typeof source === "object" ? readNestedProperty(source, path) : source;
	}

	if (isNullOrEmpty(path)) {
		return null;
	}

	throw new Error(`The message requires the property '${path}', but there is no value for it in the source object. The source object is: ${JSON.stringify(source)}`);
}

function makeSubFormats(culture: Locale, subMsgFormat: Record<string, ParserArgument[]>, parent: ParserArgument | null): Record<string, ParserDelegate> {
	const i: Record<string, ParserDelegate> = {};
	for (const set of Object.keys(subMsgFormat)) {
		i[set] = interpretSubs(culture, subMsgFormat[set], parent);
	}

	return i;
}

export default function interpretSubs(culture: Locale, elements: ParserArgument[], parent: ParserArgument | null): ParserDelegate {
	const result = new Array(elements.length);
	const l = elements.length;
	let i = -1;

	while (++i < l) {
		result[i] = interpretElement(culture, elements[i], parent);
	}

	//optimizo casos más comunes
	switch (result.length) {
		case 0:
			return () => "";

		case 1:
			return result[0];

		case 2:
			return (obj) => {
				return result[0](obj) + result[1](obj);
			}

		case 3:
			return (obj) => {
				return result[0](obj) + result[1](obj) + result[2](obj);
			}
	}

	return (obj) => {
		return result.reduce((sb, x) => sb + x(obj), "");
	};
}
